import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useParams, useHistory } from 'react-router-dom'
import { Container, Box, Typography, Button, Paper } from '@material-ui/core'
import moment from 'moment'
import { getOrder, updateOrder } from '../../actions/orderActions'
import Spinner from '../Spinner/Spinner'
import styles from '../Clients/Clients.module.css'
import { selectAuthData } from '../../store/selectors/authSelectors'
import PaymentHistory from '../InvoiceDetails/PaymentHistory'

const currency = (val) => {
  const num = Number(val) || 0
  return num.toLocaleString('en-US', { style: 'currency', currency: 'USD' })
}

const drivePreviewUrl = (url) => {
  if (!url) return ''
  try {
    const u = new URL(url)
    if (u.hostname.includes('drive.google.com')) {
      // Handle formats: /file/d/{id}/view or /uc?id={id}
      const pathParts = u.pathname.split('/')
      const fileIndex = pathParts.findIndex((p) => p === 'd')
      const idFromPath = fileIndex >= 0 ? pathParts[fileIndex + 1] : null
      const idFromQuery = u.searchParams.get('id')
      const fileId = idFromPath || idFromQuery
      if (fileId) return `https://drive.google.com/file/d/${fileId}/preview`
    }
    return url
  } catch (e) {
    return url
  }
}

const statusStyle = (status) => {
  const base = { borderRadius: 12, padding: '4px 10px', fontWeight: 600, fontSize: 12, color: '#0f172a' }
  if (status === 'Paid') return { ...base, backgroundColor: '#b0f2c2', color: '#065f46' }
  if (status === 'Partial') return { ...base, backgroundColor: '#cde5ff', color: '#0b4f91' }
  if (status === 'Booked') return { ...base, backgroundColor: '#fef3c7', color: '#92400e' }
  return { ...base, backgroundColor: '#fecdd3', color: '#991b1b' } // Unpaid/default
}

const OrderDetails = () => {
  const { id } = useParams()
  const history = useHistory()
  const dispatch = useDispatch()
  const auth = useSelector(selectAuthData)
  const { order, isLoading } = useSelector((state) => state.orders || {})
  const payments = Array.isArray(order?.payments) ? order.payments : []
  const paymentsWithIds = payments.map((p, idx) => ({ _id: p._id || String(idx), ...p }))

  const handleUpdatePayment = async (updatedRecord) => {
    if (!order?._id) return
    const nextPayments = paymentsWithIds.map((p) => {
      if ((p._id || '') === updatedRecord._id) {
        const { _id, ...rest } = updatedRecord
        return { ...p, ...rest }
      }
      return p
    })
    await dispatch(updateOrder(order._id, { payments: nextPayments }))
  }

  useEffect(() => {
    if (id) dispatch(getOrder(id))
  }, [id, dispatch])

  if (!auth) {
    history.push('/login')
    return null
  }

  if (isLoading || !order) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', paddingTop: 32 }}>
        <Spinner />
      </div>
    )
  }

  const paidTotal = payments.reduce((sum, p) => sum + (Number(p.amountPaid) || 0), 0)
  const remaining = (Number(order.total) || 0) - paidTotal

  return (
    <div className={`${styles.pageLayout} order-details-page`}>
      <Container style={{ width: '90%', maxWidth: 1200, paddingTop: 24 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <div>
            <Typography variant="h5">Order Details</Typography>
            <Typography variant="body2" color="textSecondary">
              Invoice #{order.invoiceNumber} {order.clientPONumber ? `• Client PO ${order.clientPONumber}` : ''}
            </Typography>
          </div>
          <Box display="flex" alignItems="center" style={{ gap: 12 }}>
            <span style={statusStyle(order.status)}>{order.status}</span>
            {order.invoiceId && (
              <Button
                variant="contained"
                color={order.invoiceDeleted ? "secondary" : "primary"}
                disabled={order.invoiceDeleted}
                onClick={() => !order.invoiceDeleted && history.push(`/invoice/${order.invoiceId}`)}
              >
                {order.invoiceDeleted ? "Deleted Invoice" : "View Invoice"}
              </Button>
            )}
          </Box>
        </Box>

        <Box display="grid" gridTemplateColumns="repeat(auto-fit,minmax(240px,1fr))" gridGap={16} mb={3}>
          <Paper style={{ padding: 16 }}>
            <Typography variant="subtitle2" color="textSecondary">Totals</Typography>
            <Typography variant="body1">Order Total: {currency(order.total)}</Typography>
            <Typography variant="body1">Paid: {currency(paidTotal)}</Typography>
            <Typography variant="body1">Remaining: {currency(Math.max(remaining, 0))}</Typography>
          </Paper>
          <Paper style={{ padding: 16 }}>
            <Box display="flex" flexWrap="wrap" alignItems="flex-start" style={{ gap: 16, marginBottom: 8 }}>
              <div>
                <Typography variant="subtitle2" color="textSecondary">Invoice #</Typography>
                <Typography variant="body1">{order.invoiceNumber || '—'}</Typography>
              </div>
              <div>
                <Typography variant="subtitle2" color="textSecondary">PC Invoice</Typography>
                <Typography variant="body1">{order.pcInvoiceNumber || '—'}</Typography>
              </div>
              <div>
                <Typography variant="subtitle2" color="textSecondary">Client PO</Typography>
                <Typography variant="body1">{order.clientPONumber || '—'}</Typography>
              </div>
            </Box>
            <Box display="flex" flexDirection="row" alignItems="center" style={{ gap: 8, marginTop: 4 }}>
              {order.link ? (
                <Button variant="outlined" color="primary" size="small" onClick={() => window.open(order.link, '_blank')}>
                  Open Link
                </Button>
              ) : (
                <Typography variant="body2" color="textSecondary">No link provided</Typography>
              )}
            </Box>
          </Paper>
        </Box>

        <Typography variant="h6" gutterBottom>Payment History</Typography>
        <PaymentHistory
          paymentRecords={paymentsWithIds}
          allowEditing
          onUpdatePayment={handleUpdatePayment}
        />

        <Paper style={{ padding: 16, marginTop: 24 }}>
          <Typography variant="h6" gutterBottom>Attachment</Typography>
          {order.link ? (
            <Box style={{ height: '80vh', minHeight: 500 }}>
              <iframe
                title="Order Attachment"
                src={drivePreviewUrl(order.link)}
                style={{ width: '100%', height: '100%', border: '1px solid #e5e7eb', borderRadius: 8 }}
                allow="autoplay"
              />
            </Box>
          ) : (
            <Typography variant="body2" color="textSecondary">No attachment link provided.</Typography>
          )}
        </Paper>
      </Container>
    </div>
  )
}

export default OrderDetails
